import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDownIcon, ArrowPathIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { apiRequest } from '../../utils/api';
import { clearAnalysisFilters } from '../analysis/utils/analysisFilters';

const GscPropertySelector = ({
  gscProperties = [],
  currentGscProperty,
  onGscPropertySelect,
  darkMode = false,
  onPropertiesRefresh,
}) => {
  const navigate = useNavigate();
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [isConnecting, setIsConnecting] = React.useState(false);
  const [error, setError] = React.useState('');

  const [searchTerm, setSearchTerm] = React.useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('');

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const filteredProperties = React.useMemo(() => {
    if (!debouncedSearchTerm) return gscProperties;
    return gscProperties.filter(prop =>
      (prop.displayName || prop.siteUrl).toLowerCase().includes(debouncedSearchTerm.toLowerCase()) ||
      prop.siteUrl.toLowerCase().includes(debouncedSearchTerm.toLowerCase())
    );
  }, [gscProperties, debouncedSearchTerm]);

  const handleSelectChange = (event) => {
    const newProperty = event.target.value;
    if (newProperty !== currentGscProperty) {
      clearAnalysisFilters();
      navigate('/overview');
      onGscPropertySelect(newProperty);
    }
  };

  const handleConnectGSC = async () => {
    setIsConnecting(true);
    setError('');
    try {
      sessionStorage.setItem('authRedirect', window.location.pathname);
      const response = await apiRequest('/api/integrations/gsc/connect/url/');
      const data = await response.json();
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        throw new Error(data.error || 'Failed to get authorization URL');
      }
    } catch (err) {
      setError(err.message || 'Failed to connect');
      setIsConnecting(false);
    }
  };

  const handleRefreshProperties = async () => {
    setIsRefreshing(true);
    try {
      const response = await apiRequest('/api/analyze/gsc/refresh/', {
        method: 'POST'
      });
      if (response.ok) {
        const data = await response.json();
        if (onPropertiesRefresh) {
          onPropertiesRefresh(data);
        }
      }
    } catch (err) {
      console.error('Error refreshing GSC properties:', err);
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!gscProperties || gscProperties.length === 0) {
    return (
      <div className={`p-3 rounded-lg border ${
        darkMode
          ? 'bg-gray-800 border-gray-700'
          : 'bg-gray-50 border-gray-200'
      }`}>
        <div className="text-center">
          <div className={`text-xs font-medium mb-2 ${
            darkMode ? 'text-gray-200' : 'text-gray-900'
          }`}>
            No Search Console properties available
          </div>
          <div className={`text-xs mb-3 ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}>
            Connect a Google account with Search Console access to unlock GSC reports.
          </div>

          <button
            onClick={handleConnectGSC}
            disabled={isConnecting}
            className={`
              w-full flex items-center justify-center gap-2.5 px-4 py-2.5
              bg-white hover:bg-gray-50
              border border-gray-300 rounded-lg shadow-sm hover:shadow
              transition-all text-sm
              ${isConnecting ? 'opacity-70 cursor-not-allowed' : ''}
            `}
          >
            {isConnecting ? (
              <>
                <div className="w-4 h-4 border-2 border-gray-200 border-t-blue-500 rounded-full animate-spin" />
                <span className="text-xs font-medium text-gray-600">Connecting...</span>
              </>
            ) : (
              <>
                <svg width="16" height="16" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                  <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                  <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                  <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                </svg>
                <span className="text-xs font-medium text-gray-700">Connect Search Console</span>
              </>
            )}
          </button>

          {error && (
            <div className={`mt-2 text-xs p-2 rounded ${
              darkMode
                ? 'bg-red-900/20 text-red-400 border border-red-800'
                : 'bg-red-50 text-red-600 border border-red-200'
            }`}>
              {error}
            </div>
          )}

          <div className={`mt-2 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            <div className="mb-1">After connecting, make sure you have:</div>
            <div className="text-left space-y-0.5">
              <p>• A website added to <a
                href="https://search.google.com/search-console"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-500 hover:text-blue-600 underline"
              >Search Console</a></p>
              <p>• Verified ownership of your site</p>
              <p>• Wait 24-48 hours for data to appear</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {gscProperties.length > 5 && (
        <div className="relative">
          <div className={`absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none ${
            darkMode ? 'text-gray-400' : 'text-gray-500'
          }`}>
            <MagnifyingGlassIcon className="h-3.5 w-3.5" />
          </div>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={`Search ${gscProperties.length} properties...`}
            className={`w-full pl-9 pr-8 py-2 text-xs border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
              darkMode
                ? 'bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500'
                : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500 focus:border-blue-500'
            }`}
          />
          {searchTerm && (
            <button
              onClick={() => setSearchTerm('')}
              className={`absolute inset-y-0 right-0 pr-3 flex items-center ${
                darkMode ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <XMarkIcon className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      )}

      <div className="relative w-full">
        <select
          value={searchTerm ? '' : (currentGscProperty || '')}
          onChange={handleSelectChange}
          className={`w-full appearance-none block pl-3 pr-8 py-2 text-xs border rounded-md shadow-sm focus:outline-none focus:ring-1 ${
            darkMode
              ? 'bg-gray-700 border-gray-600 text-gray-100 focus:ring-blue-500 focus:border-blue-500'
              : 'bg-white border-gray-300 text-gray-900 focus:ring-blue-500 focus:border-blue-500'
          }`}
        >
          {filteredProperties.length === 1 ? (
            <>
              <option value="" disabled>Select this property:</option>
              <option key={filteredProperties[0].siteUrl} value={filteredProperties[0].siteUrl}>
                ✓ {filteredProperties[0].displayName || filteredProperties[0].siteUrl}
              </option>
            </>
          ) : (
            <>
              <option value="" disabled>
                {currentGscProperty ? 'Change property...' : 'Select a property...'}
              </option>
              {filteredProperties.map((prop) => (
                <option key={prop.siteUrl} value={prop.siteUrl}>
                  {prop.displayName || prop.siteUrl}
                </option>
              ))}
            </>
          )}
        </select>
        <div className={`pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 ${
          darkMode ? 'text-gray-300' : 'text-gray-500'
        }`}>
          <ChevronDownIcon className="h-4 w-4" />
        </div>
      </div>

      {gscProperties.length > 5 && searchTerm && (
        <div className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Showing {filteredProperties.length} of {gscProperties.length} properties
        </div>
      )}

      <button
        onClick={handleRefreshProperties}
        disabled={isRefreshing}
        className={`w-full flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs rounded-md border transition-colors ${
          darkMode
            ? 'border-gray-600 text-gray-300 hover:bg-gray-700 disabled:opacity-50'
            : 'border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-50'
        }`}
        title="Refresh properties from Google Search Console"
      >
        <ArrowPathIcon className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
        {isRefreshing ? 'Refreshing...' : 'Refresh Properties'}
      </button>
    </div>
  );
};

export default React.memo(GscPropertySelector);
